import { observable, action, computed } from "mobx";
import { makeApi, URLS } from "Utils/apiURL";

class DeepResearchStore {
  @observable insightsData = {};
  @observable profileData = {};
  @observable companyData = {};
  @observable companyInsights = {};
  @observable deepDiveIntel = {};
  @observable dataQuality = {};
  @observable callNowSynthesis = {};

  @observable conversationStarters = [];
  @observable sellingSignals = [];
  @observable structuredProfile = {};
  @observable serpLinks = {};
  @observable deepDiveExtraction = {};
  // Dynamic per-prospect SERP queries generated by DynamicQueryService.
  // Surfaced in the Sources panel as "why these searches" rationale.
  @observable dynamicQueries = []; // List<string>
  @observable dynamicReasoning = "";
  // Tracks per-URL crawl status streamed in via progress events:
  // "crawled" once the page body is fetched, "extracted" once intel is parsed.
  @observable crawlStatus = {};
  // Set of SERP labels already received via streaming, used to animate bucket
  // appearance without re-running animation when other state changes.
  @observable receivedSerpBuckets = [];
  @observable researchProgress = []; // raw event log (for debugging / replay)
  @observable chatHistory = [];

  @observable isLoading = false;
  @observable loadingPhase = ""; // "researching" | "personalizing" | "complete" | "error"
  @observable errorMessage = "";
  @observable elapsedTime = 0;
  @observable timerInterval = null;

  @observable currentLinkedInUrl = "";
  @observable currentContactName = "";
  @observable currentContactId = "";

  // Multi-profile support
  @observable sellerProfiles = [];
  @observable sellerProfilesLoaded = false;
  @observable sellerProfilesLoading = false;
  @observable activeSellerProfileId = null; // dropdown selection (next research)
  @observable loadedResearchSellerProfileId = null; // profile id used for currently-loaded research data

  _sellerFetchPromise = null;

  @computed get activeSellerProfile() {
    if (!this.activeSellerProfileId) return null;
    return this.sellerProfiles.find(p => p.id === this.activeSellerProfileId) || null;
  }

  // Background research — persists across navigation
  @observable backgroundResearchContactId = null;
  @observable backgroundResearchRunning = false;
  @observable backgroundResearchTimer = 0;
  @observable backgroundResearchMessages = [];
  @observable backgroundTimerInterval = null;
  @observable backgroundMsgInterval = null;

  @computed get hasData() {
    return Object.keys(this.insightsData).length > 0;
  }

  @computed get highUrgencySignals() {
    return this.sellingSignals.filter(s => s.urgency === "high");
  }

  @computed get highConfidenceStarters() {
    return this.conversationStarters.filter(s => s.confidence === "high");
  }

  @action
  startTimer = () => {
    this.elapsedTime = 0;
    this.timerInterval = setInterval(() => {
      this.elapsedTime += 1;
    }, 1000);
  };

  @action
  stopTimer = () => {
    if (this.timerInterval) {
      clearInterval(this.timerInterval);
      this.timerInterval = null;
    }
  };

  @action
  setLoading = (loading, phase = "") => {
    this.isLoading = loading;
    this.loadingPhase = phase;
    if (loading) {
      this.startTimer();
    } else {
      this.stopTimer();
    }
  };

  @action
  setProfileResult = (data) => {
    if (!data) return;

    this.profileData = data;
    this.dataQuality = data.data_quality || {};
    this.companyData = data.company_data || {};
    this.companyInsights = data.company_insights || {};
    this.deepDiveIntel = data.deep_dive_intel || {};
    this.callNowSynthesis = data.call_now_synthesis || {};

    const userInsights = data.user_insights || {};
    this.insightsData = userInsights;
    this.structuredProfile = userInsights.structured_profile || data.ai_insights || {};
    this.conversationStarters = userInsights.conversation_starters || [];
    this.sellingSignals = userInsights.selling_opportunities || [];
    this.serpLinks = data.serp_links || {};
    this.deepDiveExtraction = data.deep_dive_extraction || data.deepDiveExtraction || {};

    // On a completed-research load, mark every triaged URL as "extracted" so
    // the Sources panel renders the right badges without waiting for events.
    const extractedUrls = new Set();
    const dd = this.deepDiveExtraction;
    if (dd && typeof dd === "object") {
      // Common nested shapes — try to harvest any URL fields.
      const walk = (n) => {
        if (!n) return;
        if (typeof n === "string" && /^https?:\/\//i.test(n)) extractedUrls.add(n);
        else if (Array.isArray(n)) n.forEach(walk);
        else if (typeof n === "object") Object.values(n).forEach(walk);
      };
      walk(dd);
    }
    const nextStatus = {};
    extractedUrls.forEach(u => { nextStatus[u] = "extracted"; });
    this.crawlStatus = nextStatus;

    // Track which seller profile was used for this research, and default the active selection to it.
    if (data.seller_profile_id) {
      this.loadedResearchSellerProfileId = data.seller_profile_id;
      this.activeSellerProfileId = data.seller_profile_id;
    } else {
      this.loadedResearchSellerProfileId = null;
    }

    // Rehydrate chat history if persisted on the overview record
    this.chatHistory = Array.isArray(data.chat_history) ? data.chat_history : [];
  };

  @action
  appendChatMessage = (msg) => {
    this.chatHistory = [...this.chatHistory, msg];
  };

  // Merge a server-sent progress event into the store. Idempotent — safe to
  // replay the same event log multiple times (matters because polling can pick
  // up events that have already been applied on a previous tick).
  @action
  applyProgressEvent = (evt) => {
    if (!evt || !evt.type) return;
    this.researchProgress = [...this.researchProgress, evt];

    switch (evt.type) {
      case "serp_bucket_done": {
        // evt: { type, label, results: [{title, link, snippet, source, date}] }
        const label = evt.label;
        if (!label || !Array.isArray(evt.results)) break;
        this.serpLinks = { ...this.serpLinks, [label]: evt.results };
        if (!this.receivedSerpBuckets.includes(label)) {
          this.receivedSerpBuckets = [...this.receivedSerpBuckets, label];
        }
        break;
      }
      case "page_crawled": {
        // evt: { type, url, ok }
        if (evt.url) {
          this.crawlStatus = { ...this.crawlStatus, [evt.url]: "crawled" };
        }
        break;
      }
      case "page_extracted": {
        // evt: { type, url }
        if (evt.url) {
          this.crawlStatus = { ...this.crawlStatus, [evt.url]: "extracted" };
        }
        break;
      }
      case "dynamic_queries_generated": {
        // evt: { type, queries: [...], reasoning: "..." }
        if (Array.isArray(evt.queries)) this.dynamicQueries = evt.queries;
        if (typeof evt.reasoning === "string") this.dynamicReasoning = evt.reasoning;
        break;
      }
      default:
        // serp_started / triage_done / synthesis_done — log-only, no state change.
        break;
    }
  };

  @action
  setError = (msg) => {
    this.errorMessage = msg;
    this.loadingPhase = "error";
    this.isLoading = false;
    this.stopTimer();
  };

  fetchSellerProfiles = ({ force = false } = {}) => {
    if (!force && this.sellerProfilesLoaded) return Promise.resolve(this.sellerProfiles);
    if (this._sellerFetchPromise) return this._sellerFetchPromise;
    this.sellerProfilesLoading = true;
    this._sellerFetchPromise = makeApi({
      url: URLS.deepResearchListSellers,
      method: "GET"
    })
      .then(res => {
        const list = res?.data?.sellers || [];
        this._applyFetchedSellers(list);
        return this.sellerProfiles;
      })
      .catch(() => {
        this._applyFetchedSellers([]);
        return [];
      });
    return this._sellerFetchPromise;
  };

  @action
  _applyFetchedSellers = (list) => {
    this.sellerProfiles = Array.isArray(list) ? list : [];
    this.sellerProfilesLoaded = true;
    this.sellerProfilesLoading = false;
    this._sellerFetchPromise = null;

    // If the active id no longer exists, clear it
    if (this.activeSellerProfileId &&
        !this.sellerProfiles.find(p => p.id === this.activeSellerProfileId)) {
      this.activeSellerProfileId = null;
    }
    // Note: don't auto-pick a default profile here. Callers (e.g. the landing
    // page in DeepResearch.js) are responsible for choosing a sensible default
    // for *new* research, while loaded legacy research without a seller_profile_id
    // intentionally stays unselected.
  };

  @action
  setActiveSellerProfileId = (id) => {
    this.activeSellerProfileId = id || null;
  };

  saveSellerProfile = (profile) => {
    return makeApi({
      url: URLS.deepResearchSaveSeller,
      method: "POST",
      data: profile
    }).then(res => {
      const saved = res?.data;
      if (saved && saved.id) {
        this._upsertSellerProfile(saved);
        this.activeSellerProfileId = saved.id;
      }
      return saved;
    });
  };

  deleteSellerProfile = (id) => {
    if (!id) return Promise.resolve(false);
    return makeApi({
      url: URLS.deepResearchDeleteSeller.replace("{id}", id),
      method: "DELETE"
    }).then(res => {
      if (res?.data?.deleted) {
        this._removeSellerProfile(id);
        return true;
      }
      return false;
    });
  };

  @action
  _removeSellerProfile = (id) => {
    this.sellerProfiles = this.sellerProfiles.filter(p => p.id !== id);
    if (this.activeSellerProfileId === id) this.activeSellerProfileId = null;
    if (this.loadedResearchSellerProfileId === id) this.loadedResearchSellerProfileId = null;
  };

  @action
  _upsertSellerProfile = (saved) => {
    const idx = this.sellerProfiles.findIndex(p => p.id === saved.id);
    if (idx >= 0) {
      this.sellerProfiles = [
        ...this.sellerProfiles.slice(0, idx),
        saved,
        ...this.sellerProfiles.slice(idx + 1)
      ];
    } else {
      this.sellerProfiles = [...this.sellerProfiles, saved];
    }
    this.sellerProfilesLoaded = true;
  };

  @action
  reset = () => {
    this.insightsData = {};
    this.profileData = {};
    this.companyData = {};
    this.companyInsights = {};
    this.deepDiveIntel = {};
    this.dataQuality = {};
    this.callNowSynthesis = {};
    this.conversationStarters = [];
    this.sellingSignals = [];
    this.structuredProfile = {};
    this.serpLinks = {};
    this.deepDiveExtraction = {};
    this.crawlStatus = {};
    this.receivedSerpBuckets = [];
    this.researchProgress = [];
    this.dynamicQueries = [];
    this.dynamicReasoning = "";
    this.isLoading = false;
    this.loadingPhase = "";
    this.errorMessage = "";
    this.elapsedTime = 0;
    this.currentLinkedInUrl = "";
    this.currentContactName = "";
    this.currentContactId = "";
    this.loadedResearchSellerProfileId = null;
    this.chatHistory = [];
    // Don't reset sellerProfiles list, activeSellerProfileId, mode, or background research — they persist
    this.stopTimer();
  };

  @action
  startBackgroundResearch = (contactId) => {
    this.backgroundResearchContactId = contactId;
    this.backgroundResearchRunning = true;
    this.backgroundResearchTimer = 0;
    this.backgroundResearchMessages = [];

    this.backgroundTimerInterval = setInterval(() => {
      this.backgroundResearchTimer += 0.1;
    }, 100);

    const msgs = [
      "🔎 Searching the web for public profiles…",
      "📇 Mapping social handles and digital footprint...",
      "🎯 Highlighting milestones and achievements...",
      "🧠 Analyzing LinkedIn content patterns...",
      "📚 Reading career journey and transitions...",
      "🎙️ Looking for mentions in podcasts and events...",
      "🖇️ Scanning community memberships...",
      "💼 Reviewing company market position...",
      "🌐 Searching news, blogs, and forums...",
      "📊 Generating talking points and insights...",
      "✅ Almost there… finalizing summary!"
    ];
    let i = 0;
    this.backgroundMsgInterval = setInterval(() => {
      if (i < msgs.length) {
        this.backgroundResearchMessages = msgs.slice(0, i + 1);
        i++;
      } else {
        clearInterval(this.backgroundMsgInterval);
      }
    }, 4000);
  };

  @action
  stopBackgroundResearch = () => {
    this.backgroundResearchRunning = false;
    if (this.backgroundTimerInterval) {
      clearInterval(this.backgroundTimerInterval);
      this.backgroundTimerInterval = null;
    }
    if (this.backgroundMsgInterval) {
      clearInterval(this.backgroundMsgInterval);
      this.backgroundMsgInterval = null;
    }
  };

  @action
  isResearchingContact = (contactId) => {
    return this.backgroundResearchRunning && this.backgroundResearchContactId === contactId;
  };
}

const deepResearchStore = new DeepResearchStore();
export { deepResearchStore };
export default deepResearchStore;
